import React, { useState, useEffect, useRef } from "react";
import {
    Card,
    Dimmer,
    Header,
    Icon,
    Label,
    Loader,
    Segment,
} from "semantic-ui-react";
import isEmpty from "lodash/isEmpty";
import each from "lodash/each";
import NotationHeader from "../header/NotationHeader";
import Measure from "react-measure";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { LoadingState } from "../../services/http";
import AdBlockDetect from "react-ad-block-detect";
import Transition from "react-transition-group/Transition";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import { RendererContainer } from "./../../components/Renderer/Renderer.container";
import { clearTrackAnimation, initialiseAnimations } from "./NotationAnimation";
import { AppstoreLinks } from "./../../components/notation/AppstoreLinks";
import { isMobile } from "react-device-detect";

const ANIMATION_DONE_EVENT = "animation::done";

const DynamicNotationContainer = ({
    instruments,
    notation: propsNotation,
    bus,
    demoplayer,
    currentSongIndex,
    songsCount,
    jiggleLeft,
    jiggleRight,
    onPreviousSong,
    onNextSong,
    slideDirection,
}) => {
    const [selectedVariationIndex, setSelectedVariationIndex] = useState(0);
    const [selectedInstrumentIndex, setSelectedInstrumentIndex] =
        useState(undefined);
    const [selectedRange, setSelectedRange] = useState(undefined);
    const [selectedTempo, setSelectedTempo] = useState(undefined);
    const [notationContainerHeight, setNotationContainerHeight] =
        useState(undefined);
    const [isInstrumentLoading, setIsInstrumentLoading] = useState(false);
    const [hasInstrumentLoadingFailed, setHasInstrumentLoadingFailed] =
        useState(false);
    const [notation, setNotation] = useState(propsNotation);
    const [dimensions, setDimensions] = useState(undefined);
    const [gridContainer, setGridContainer] = useState(undefined);

    const animationsRef = useRef([]);

    // Replace componentWillMount - initialize notation from props
    useEffect(() => {
        setNotation(propsNotation);
    }, []);

    // Replace componentWillReceiveProps - handle notation changes
    useEffect(() => {
        clearTrackAnimation(animationsRef.current);
        const songHasChanged = notation.id !== propsNotation.id;
        if (songHasChanged) {
            setNotation(propsNotation);
            setSelectedVariationIndex(0);
            // Keep instrument, range, tempo - user preference persists across songs
        }
    }, [propsNotation.id]);

    /**
     * Function passed to the NotationHeader as a prop
     * @param event
     * @param data
     */
    const variationChanged = (event, data) => {
        setSelectedVariationIndex(data.value);
        setSelectedInstrumentIndex(undefined);
        setSelectedRange(undefined);
    };

    /**
     * Function passed to the NotationHeader as a prop
     * @param event
     * @param data
     */
    const tempoChanged = (event, data) => {
        setSelectedTempo(data.value);
    };

    const notifyInstrumentLoadingState = (state) => {
        setIsInstrumentLoading(state === LoadingState.PENDING);
        setHasInstrumentLoadingFailed(state === LoadingState.FAILED);
    };

    const pauseAnimation = () => {
        if (animationsRef.current) {
            each(animationsRef.current, (trackAnimation) => {
                trackAnimation.pause();
            });
        }
    };

    const playAnimation = (selectedTempo, variation) => {
        animationsRef.current = initialiseAnimations(
            animationsRef.current,
            gridContainer,
            selectedTempo,
            variation
        );
    };

    const stopAnimation = () => {
        clearTrackAnimation(animationsRef.current);
    };

    const restartAnimation = () => {
        if (animationsRef.current) {
            each(animationsRef.current, (trackAnimation) => {
                trackAnimation.restart();
            });
        }
    };

    if (isEmpty(notation.variations)) {
        return <NoVariations notation={notation} />;
    }

    let variation = notation.variations[selectedVariationIndex];

    return (
        <Dimmer.Dimmable
            as={Segment}
            dimmed={isInstrumentLoading}
            style={{
                padding: "0",
                border: "1px solid #e6ecf5",
                borderRadius: "5px",
                boxShadow: "unset",
            }}
        >
            <Dimmer active={isInstrumentLoading} inverted>
                <Loader>Loading Instruments...</Loader>
            </Dimmer>
            <Card
                className="ubuntuFont"
                style={{
                    borderRadius: "5px",
                    width: "100%",
                    boxShadow: "none",
                    marginTop: "0",
                }}
            >
                <Card.Content style={{ padding: "0px" }}>
                    {hasInstrumentLoadingFailed ? (
                        <AdBlockDetect>
                            <Label basic color="red">
                                Sound files may be blocked by your adblocker,
                                please disable it for this site. We don't
                                display adverts!
                            </Label>
                        </AdBlockDetect>
                    ) : (
                        ""
                    )}
                    <NotationHeader
                        notifyInstrumentLoadingState={
                            notifyInstrumentLoadingState
                        }
                        bus={bus}
                        instruments={instruments}
                        variation={variation}
                        notation={notation}
                        demoplayer={demoplayer}
                        selectedTempo={selectedTempo}
                        tempoChanged={tempoChanged}
                        variationChanged={variationChanged}
                        pauseAnimation={pauseAnimation}
                        playAnimation={playAnimation}
                        restartAnimation={restartAnimation}
                        stopAnimation={stopAnimation}
                        currentSongIndex={currentSongIndex}
                        songsCount={songsCount}
                        jiggleLeft={jiggleLeft}
                        jiggleRight={jiggleRight}
                        onPreviousSong={onPreviousSong}
                        onNextSong={onNextSong}
                    />
                </Card.Content>

                <div
                    style={{
                        position: "relative",
                        minHeight: "600px",
                        borderTop: "1px solid rgba(34,36,38,.1)",
                    }}
                >
                    <TransitionGroup component={null}>
                        <CSSTransition
                            key={notation.id}
                            timeout={450}
                            classNames={`song-slide-${
                                slideDirection || "next"
                            }`}
                        >
                            <div>
                                <Card.Content
                                    style={{
                                        paddingBottom: "0.5em",
                                        paddingTop: "1em",
                                    }}
                                >
                                    <div
                                        style={{
                                            position: "relative",
                                            minHeight: "60px",
                                        }}
                                    >
                                        <div
                                            style={{
                                                position: "absolute",
                                                left: "50%",
                                                top: "10px",
                                                transform: "translateX(-50%)",
                                                textAlign: "center",
                                                textDecoration: "underline",
                                            }}
                                        >
                                            <h2
                                                className="ubuntuFont"
                                                style={{
                                                    fontSize: "1.6rem",
                                                    margin: 0,
                                                }}
                                            >
                                                {notation.title}
                                            </h2>
                                        </div>
                                    </div>
                                </Card.Content>
                                <Card.Content
                                    style={{
                                        borderTop: "0px",
                                        paddingTop: "0",
                                    }}
                                >
                                    <Measure
                                        bounds
                                        onResize={(contentRect) => {
                                            setDimensions(contentRect.bounds);
                                        }}
                                    >
                                        {({ measureRef }) => (
                                            <div
                                                ref={measureRef}
                                                className={
                                                    !isMobile
                                                        ? "notationContainer"
                                                        : ""
                                                }
                                                style={
                                                    demoplayer
                                                        ? {
                                                              minHeight:
                                                                  "540px",
                                                              height: "auto",
                                                          }
                                                        : {
                                                              minHeight:
                                                                  "540px",
                                                              paddingBottom:
                                                                  "50px",
                                                          }
                                                }
                                            >
                                                <div
                                                    style={{
                                                        fontSize: "14px",
                                                        marginBottom: "0.5em",
                                                        marginLeft: "10px",
                                                        color: "#515365",
                                                    }}
                                                >
                                                    <a
                                                        style={{
                                                            color: "#515365",
                                                            textDecoration:
                                                                "none",
                                                        }}
                                                        href={`/browse/list?artist=${notation.artist.id}`}
                                                    >
                                                        {notation.artist.name}
                                                    </a>
                                                </div>
                                                <Transition
                                                    unmountOnExit
                                                    appear
                                                    addEndListener={(
                                                        node,
                                                        done
                                                    ) =>
                                                        node.addEventListener(
                                                            ANIMATION_DONE_EVENT,
                                                            done
                                                        )
                                                    }
                                                    onEnter={(node, done) => {
                                                        setGridContainer(node);
                                                    }}
                                                    in={true}
                                                    timeout={1}
                                                >
                                                    <div>
                                                        {dimensions ? (
                                                            isMobile ? (
                                                                <AppstoreLinks />
                                                            ) : (
                                                                <RendererContainer
                                                                    variation={
                                                                        variation
                                                                    }
                                                                    height={Math.max(
                                                                        dimensions.height,
                                                                        490
                                                                    )}
                                                                    width={
                                                                        dimensions.width +
                                                                        10
                                                                    }
                                                                />
                                                            )
                                                        ) : (
                                                            <div />
                                                        )}
                                                    </div>
                                                </Transition>
                                            </div>
                                        )}
                                    </Measure>
                                </Card.Content>
                            </div>
                        </CSSTransition>
                    </TransitionGroup>
                </div>
            </Card>
        </Dimmer.Dimmable>
    );
};

const NoVariations = (props) => (
    <Segment padded={true}>
        <Header as="h4" icon textAlign="center">
            <Icon name="mute" circular />
            <Header.Content>
                We are in the process of adding notation to{" "}
                <b style={{ textDecoration: "underline" }}>
                    {props.notation.title}
                </b>
                .
                <br />
                Please come back later.
            </Header.Content>
        </Header>
    </Segment>
);

export default DynamicNotationContainer;
